import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Scan, CalendarBlank, Trophy, FileCsv } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLang } from "../context/LangContext";
import api from "../lib/api";

export const ScanStats = () => {
  const { t, lang } = useLang();
  const [stats, setStats] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    api.get("/stats/scans").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (!stats) return null;

  const exportCsv = async () => {
    try {
      const res = await api.get(`/stats/scans/export?lang=${lang}&t=${Date.now()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "scansioni-certificoqr.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(t.stats.exportError); }
  };

  const max = Math.max(...stats.months.map((m) => m.count), 1);
  const monthLabel = (ym) =>
    new Date(ym + "-01T00:00:00").toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { month: "short" });

  const cards = [
    { icon: Scan, label: t.stats.total, value: stats.total, testid: "stats-total" },
    { icon: CalendarBlank, label: t.stats.thisMonth, value: stats.this_month, testid: "stats-this-month" },
    { icon: Trophy, label: t.stats.top, value: stats.top_machine ? `${stats.top_machine.name}` : "—", sub: stats.top_machine ? `${stats.top_machine.count} ${stats.top_machine.count === 1 ? t.scans.one : t.scans.badge}` : null, testid: "stats-top-machine" },
  ];

  return (
    <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className="mb-10 rounded-md border border-slate-200 bg-white p-6" data-testid="scan-stats-panel">
      <div className="flex items-center justify-between gap-2">
        <p className="font-mono-tech text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600">[ {t.stats.title} ]</p>
        {stats.total > 0 && (
          <button onClick={exportCsv}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-emerald-500 hover:text-emerald-700 transition-colors"
            data-testid="stats-export-csv">
            <FileCsv size={14} /> {t.stats.exportCsv}
          </button>
        )}
      </div>
      <div className="mt-5 grid sm:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.testid} className="rounded-md bg-[#F8F9FA] border border-slate-100 p-4" data-testid={c.testid}>
            <div className="flex items-center gap-2 text-slate-500">
              <c.icon size={15} className="text-emerald-600" />
              <span className="font-mono-tech text-[10px] uppercase tracking-wider">{c.label}</span>
            </div>
            <p className="mt-2 font-display font-bold text-2xl text-[#0F172A] truncate">{c.value}</p>
            {c.sub && <p className="text-[11px] text-slate-500">{c.sub}</p>}
          </div>
        ))}
      </div>
      <div className="mt-6" data-testid="stats-month-chart">
        <p className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-slate-400">{t.stats.chart}</p>
        <div className="mt-3 flex items-end gap-1.5 h-28">
          {stats.months.map((m) => (
            <div key={m.month} className="flex-1 flex flex-col items-center gap-1" data-testid={`stats-bar-${m.month}`}>
              <span className="font-mono-tech text-[9px] text-slate-500 h-3">{m.count > 0 ? m.count : ""}</span>
              <div className={`w-full rounded-sm ${m.count > 0 ? "bg-emerald-500" : "bg-slate-200"}`}
                style={{ height: `${m.count > 0 ? Math.max((m.count / max) * 72, 8) : 3}px` }} />
              <span className="font-mono-tech text-[9px] text-slate-400">{monthLabel(m.month)}</span>
            </div>
          ))}
        </div>
      </div>
      {stats.recent && stats.recent.length > 0 && (
        <div className="mt-5">
          <button onClick={() => setShowDetail(!showDetail)}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-emerald-500 hover:text-emerald-700 transition-colors"
            data-testid="stats-detail-toggle">
            {showDetail ? t.stats.hideDetail : t.stats.showDetail}
          </button>
          {showDetail && (
            <ul className="mt-3 divide-y divide-slate-100 max-h-72 overflow-y-auto" data-testid="stats-detail-list">
              {stats.recent.map((s, i) => (
                <li key={i} className="flex items-center gap-2.5 py-2" data-testid={`stats-detail-row-${i}`}>
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                  <span className="font-mono-tech text-xs text-slate-600">
                    {new Date(s.ts).toLocaleString(lang === "it" ? "it-IT" : "en-GB", { dateStyle: "medium", timeStyle: "short" })}
                  </span>
                  <span className="text-xs font-medium text-[#0F172A] truncate">{s.machine}</span>
                  {(s.city || s.country) && (
                    <span className="ml-auto text-[11px] text-slate-400 shrink-0">{[s.city, s.country].filter(Boolean).join(", ")}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </motion.section>
  );
};
